import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import moment from "moment";

// 📌 FETCH GRN LIST (with pagination + filters)
export const fetchGRNList = createAsyncThunk(
  "grn/fetchGRNList",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
    warehouse_id,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    warehouse_id?: string;
  }) => {
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if(warehouse_id){
        params.append("warehouse_id", warehouse_id);
    }

    if (search) params.append("search", search);

    if (sort) {
      const orderingVal = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingVal);
    }

    // ✅ handle dynamic filters
    filters?.forEach(({ filterField, filterValue }: any) => {
      if (filterValue === undefined || filterValue === null || filterValue === "") return;

      // Handle date range filters (object with from_date / to_date)
      if (typeof filterValue === "object" && filterValue !== null) {
        const { from_date, to_date } = filterValue;

        if (from_date || to_date) {
          // Handle date fields: inward_date, created_at, updated_at
          if (filterField === "inward_date") {
            if (from_date) params.append("inward_date_from", from_date);
            if (to_date) params.append("inward_date_to", to_date);
          } else if (filterField === "created_at") {
            if (from_date) params.append("created_at_from", from_date);
            if (to_date) params.append("created_at_to", to_date);
          } else if (filterField === "updated_at") {
            if (from_date) params.append("updated_at_from", from_date);
            if (to_date) params.append("updated_at_to", to_date);
          }
        } else {
          // Handle other object filters
          params.append(filterField, JSON.stringify(filterValue));
        }
      } else {
        // Handle primitive values (string, number)
        params.append(filterField, String(filterValue));
      }
    });

    const res: any = await Axios.get(
      `${api.warehouse2.iteminward.list}?${params.toString()}`
    );

    return {
      data: res.data.data || [],
      rowCount: res.data.count || 0,
    };
  }
);

// 📌 CHECK IF GRN CAN BE EDITED
export const checkGRNEditable = createAsyncThunk(
  "grn/checkGRNEditable",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.get(
        `${api.warehouse2.iteminward.checkEditable}${inventory_id}`
      );
      return res?.data?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to check GRN editability");
    }
  }
);

// 📌 CHECK IF GRN CAN BE REVERTED
export const checkGRNRevertable = createAsyncThunk(
  "grn/checkGRNRevertable",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.get(
        `${api.warehouse2.iteminward.checkRevertable}${inventory_id}`
      );
      return res?.data?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to check GRN revertability");
    }
  }
);

// 📌 FETCH GRN DETAILS BY INVENTORY ID
export const fetchGRNDetails = createAsyncThunk(
  "grn/fetchGRNDetails",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.get(
        `${api.warehouse2.iteminward.getGRNByInventoryId}${inventory_id}`
      );
      return res?.data?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch GRN details");
    }
  }
);

// 📌 CREATE GRN
export const createGRN = createAsyncThunk(
  "grn/createGRN",
  async (payload: FormData, { rejectWithValue }) => {
    try {
      const res = await Axios.post(
        api.warehouse2.iteminward.create,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create GRN");
    }
  }
);

// 📌 UPDATE GRN
export const updateGRN = createAsyncThunk(
  "grn/updateGRN",
  async ({ inventory_id, payload }: { inventory_id: string; payload: FormData }, { rejectWithValue }) => {
    try {
      const res: any = await Axios.put(
        `${api.warehouse2.iteminward.update}${inventory_id}/`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update GRN");
    }
  }
);


export const updateOrderReturnGRN = createAsyncThunk(
  "grn/updateOrderReturnGRN",
  async ({ inventory_id, payload }: { inventory_id: string; payload: FormData }, { rejectWithValue }) => {
    try {
      const res: any = await Axios.put(
        `${api.warehouse?.item_inward?.sales_item_inword_update}/${inventory_id}/`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update GRN");
    }
  }
);

// 📌 DELETE GRN
export const deleteGRN = createAsyncThunk(
  "grn/deleteGRN",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${api.warehouse2.iteminward.delete}${inventory_id}/`);
      return inventory_id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 📌 REVERT GRN
export const revertGRN = createAsyncThunk(
  "grn/revertGRN",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.delete(
        `${api.warehouse2.iteminward.revert}${inventory_id}`
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to revert GRN");
    }
  }
);
export const revertOrderReturnGRN = createAsyncThunk(
  "grn/revertGRN",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.delete(
        `${api.warehouse?.item_inward?.sales_item_inword_revert}/${inventory_id}`
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to revert GRN");
    }
  }
);

// 📌 REVERT STOCK TRANSFER INWARD
export const revertStockTransferInward = createAsyncThunk(
  "grn/revertStockTransferInward",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.delete(
        `${api.warehouse2.pendingStockTransfer.revert}${inventory_id}`
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to revert stock transfer inward");
    }
  }
);

// 📌 SPLIT BAGS (Create Child Bags)
export const splitBags = createAsyncThunk(
  "grn/splitBags",
  async (
    {
      inventory_item_id,
      child_bags,
    }: {
      inventory_item_id: string;
      child_bags: Array<{ quantity: number }>;
    },
    { rejectWithValue }
  ) => {
    try {
      const res: any = await Axios.post(
        `${api.warehouse2.iteminward.splitBags}${inventory_item_id}`,
        { child_bags }
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to split bags");
    }
  }
);

// 📌 EXPORT EXCEL
export const exportGRNExcel = async ({
  page,
  pageSize,
  sort,
  order,
  search,
  filters,
  warehouse_id,
}: {
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any;
  warehouse_id?: string;
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (warehouse_id) {
    params.append("warehouse_id", warehouse_id);
  }

  if (search) params.append("search", search);

  // Handle sorting
  if (sort) {
    const orderingVal = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingVal);
  }

  // ✅ handle dynamic filters
  filters?.forEach(({ filterField, filterValue }: any) => {
    if (filterValue === undefined || filterValue === null || filterValue === "") return;

    // Handle date range filters (object with from_date / to_date)
    if (typeof filterValue === "object" && filterValue !== null) {
      const { from_date, to_date } = filterValue;

      if (from_date || to_date) {
        // Handle date fields: inward_date, created_at, updated_at
        if (filterField === "inward_date") {
          if (from_date) params.append("inward_date_from", from_date);
          if (to_date) params.append("inward_date_to", to_date);
        } else if (filterField === "created_at") {
          if (from_date) params.append("created_at_from", from_date);
          if (to_date) params.append("created_at_to", to_date);
        } else if (filterField === "updated_at") {
          if (from_date) params.append("updated_at_from", from_date);
          if (to_date) params.append("updated_at_to", to_date);
        }
        return;
      }

      // Handle other object filters
      Object.entries(filterValue).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== "") {
          params.append(key, String(val));
        }
      });
    } else {
      // Handle simple string/number filters
      params.append(filterField, String(filterValue));
    }
  });

  const res = await Axios.get(
    `${api.warehouse2.iteminward.list}?${params.toString()}`,
    { responseType: "blob" }
  );

  return {
    blob: res.data,
    filename:
      res.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || `GRN_${moment().format("YYYY-MM-DD")}.xlsx`,
  };
};


export const exportSaleOrderReturnExcel = async ({
  page,
  pageSize,
  sort,
  order,
  search,
  filters,
  warehouse_id,
}: {
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any;
  warehouse_id?: string;
}) => {
  const params = new URLSearchParams();

  params.append("export", "csv");

  if (warehouse_id) {
    params.append("warehouse_id", warehouse_id);
  }

  if (search) params.append("search", search);

  // Handle sorting
  if (sort) {
    const orderingVal = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingVal);
  }

  // ✅ handle dynamic filters
  filters?.forEach(({ filterField, filterValue }: any) => {
    if (filterValue === undefined || filterValue === null || filterValue === "") return;

    // Handle date range filters (object with from_date / to_date)
    if (typeof filterValue === "object" && filterValue !== null) {
      const { from_date, to_date } = filterValue;

      if (from_date || to_date) {
        // Handle date fields: inward_date, created_at, updated_at
        if (filterField === "inward_date") {
          if (from_date) params.append("inward_date_from", from_date);
          if (to_date) params.append("inward_date_to", to_date);
        } else if (filterField === "created_at") {
          if (from_date) params.append("created_at_from", from_date);
          if (to_date) params.append("created_at_to", to_date);
        } else if (filterField === "updated_at") {
          if (from_date) params.append("updated_at_from", from_date);
          if (to_date) params.append("updated_at_to", to_date);
        }
        return;
      }

      // Handle other object filters
      Object.entries(filterValue).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== "") {
          params.append(key, String(val));
        }
      });
    } else {
      // Handle simple string/number filters
      params.append(filterField, String(filterValue));
    }
  });

  const res = await Axios.get(
    `${api.warehouse?.item_inward?.sales_order_return_listing}?${params.toString()}`,
    { responseType: "blob" }
  );

  return {
    blob: res.data,
    filename:
      res.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||`sales_order_return_${moment().format("YYYY-MM-DD")}.csv`,
  };
};